import { apiRequest } from './client';

/** Master reporting (SRS #80) and customer reporting (SRS #81). */

const TOKEN_KEY = 'policyora_token';
const opts = (): RequestInit => {
  const token = localStorage.getItem(TOKEN_KEY);
  return { headers: token ? { Authorization: `Bearer ${token}` } : {} };
};
const qs = (p: Range) => {
  const q = new URLSearchParams();
  if (p.since) q.set('since', p.since);
  if (p.until) q.set('until', p.until);
  if (p.days) q.set('days', String(p.days));
  const s = q.toString();
  return s ? `?${s}` : '';
};

export interface Range { since?: string; until?: string; days?: number }

export interface PlatformReport {
  period: { since: string; until: string; generated_at: string };
  users: {
    total: number; new: number; active: number;
    by_country: { country: string; users: number }[];
    by_plan: { plan: string; status: string; users: number }[];
    by_industry: { industry: string; users: number }[];
  };
  adoption: Record<string, number>;
  revenue: {
    collected: string; payments: number; failed: number; refunded: number;
    by_gateway: { gateway: string; amount: string; payments: number }[];
    mrr_estimate: string; trials_started: number; conversions: number;
  };
  performance: Record<string, number | null>;
  top_organisations: { organisation: string; country: string | null; logins: number; users: number }[];
}

export interface MemberUsage {
  name: string; email: string; role: string; active: boolean; has_login: boolean;
  logins: number; last_login: string | null; searches: number; comparisons: number; downloads: number;
  chat_sessions: number; chat_messages: number; reports: number; alerts: number; assessments: number; activity: number;
}
export interface CustomerReport {
  period: { since: string; until: string; generated_at: string };
  organisation: string; plan: string | null;
  seats: { used: number; limit: number | null };
  members: MemberUsage[];
  totals: Record<string, number> & { members: number; active_members: number; adoption_rate: number };
}

export const getPlatformReport = (r: Range = {}) => apiRequest<PlatformReport>(`/api/v1/admin/reports/platform${qs(r)}`, opts());
export const getTeamUsage = (r: Range = {}) => apiRequest<CustomerReport>(`/api/v1/enterprise/reports/usage${qs(r)}`, opts());

export async function downloadReportFile(kind: 'platform' | 'usage', format: 'csv' | 'pdf', r: Range = {}): Promise<void> {
  const apiUrl = process.env.REACT_APP_API_BASE_URL ?? '';
  const path = kind === 'platform' ? `/api/v1/admin/reports/platform.${format}` : `/api/v1/enterprise/reports/usage.${format}`;
  const res = await fetch(`${apiUrl}${path}${qs(r)}`, opts());
  if (!res.ok) throw new Error(`Download failed (${res.status})`);
  const m = /filename="?([^"]+)"?/.exec(res.headers.get('content-disposition') || '');
  const href = URL.createObjectURL(await res.blob());
  const a = document.createElement('a'); a.href = href; a.download = m ? m[1] : `${kind}-report.${format}`;
  document.body.appendChild(a); a.click(); document.body.removeChild(a); URL.revokeObjectURL(href);
}

/** Shared date-range control state: default last 30 days. */
export const defaultRange = (): { since: string; until: string } => {
  const until = new Date(); const since = new Date(until.getTime() - 30 * 86400000);
  const iso = (d: Date) => d.toISOString().slice(0, 10);
  return { since: iso(since), until: iso(until) };
};

// ── customer audit trail (SRS #125) / exports (SRS #127/#128) ─────────────
export interface AuditItem {
  id: number; action: string; label: string; actor: string | null; target_type: string | null; target_id: string | null;
  target_label: string | null; detail: Record<string, unknown> | null; ip_address: string | null; created_at: string | null;
  organisation?: string | null; owner_email?: string;
}
export const getTeamAudit = (p: { action?: string; actor?: string; since?: string; until?: string; exports_only?: boolean; limit?: number; offset?: number } = {}) => {
  const q = new URLSearchParams();
  Object.entries(p).forEach(([k, v]) => { if (v !== undefined && v !== '' && v !== false) q.set(k, String(v)); });
  const s = q.toString();
  return apiRequest<{ items: AuditItem[]; labels: Record<string, string> }>(`/api/v1/enterprise/reports/audit${s ? `?${s}` : ''}`, opts());
};
export const adminListExports = (p: { since?: string; until?: string; email?: string; kind?: string; limit?: number } = {}) => {
  const q = new URLSearchParams();
  Object.entries(p).forEach(([k, v]) => { if (v !== undefined && v !== '') q.set(k, String(v)); });
  const s = q.toString();
  return apiRequest<{ exports: AuditItem[] }>(`/api/v1/admin/exports${s ? `?${s}` : ''}`, opts());
};
export async function downloadTeamFile(path: 'audit.csv' | 'export', r: Range = {}): Promise<void> {
  const apiUrl = process.env.REACT_APP_API_BASE_URL ?? '';
  const res = await fetch(`${apiUrl}/api/v1/enterprise/reports/${path}${qs(r)}`, opts());
  if (!res.ok) throw new Error(`Download failed (${res.status})`);
  const m = /filename="?([^"]+)"?/.exec(res.headers.get('content-disposition') || '');
  const href = URL.createObjectURL(await res.blob());
  const a = document.createElement('a'); a.href = href; a.download = m ? m[1] : path;
  document.body.appendChild(a); a.click(); document.body.removeChild(a); URL.revokeObjectURL(href);
}
